import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EMPTY_SHELF,
  loadSavedShelf,
  removeFromShelf,
  type SavedShelf,
  saveToShelf,
  shelfForScope,
} from '../content/shelf';
import { queryKeys } from '../query/keys';
import { useConfig, useSession } from './provider';
import { useWriteScope } from './use-global-group';
import { useMe } from './use-me';

/** Something that can be saved: a book or a Math row, by lineage and the version shown. */
export interface SavedItem {
  lineageUuid: string;
  uuid?: string;
  /** Every lineage that opens it: saved under any counts as saved, and unsaving takes off all. */
  lineages?: readonly string[];
}

/**
 * Library › My favorites: what the person saved as favorites (books and Math rows on the
 * one shelf), and saving or unsaving one. The
 * change shows at once and is undone if Core refuses it; Core's answer is read back after.
 * `available` is false where the environment has no shelf type, and the screens then offer no
 * saving at all.
 */
export function useSaved() {
  const session = useSession();
  const { strongerMemoryTypes } = useConfig();
  const shelfType = strongerMemoryTypes?.shelf;
  const me = useMe();
  const personUuid = me.data?.uuid;
  const queryClient = useQueryClient();
  const writeScope = useWriteScope();
  const key = queryKeys.savedShelf();

  const shelf = useQuery({
    queryKey: key,
    enabled:
      session.status === 'authenticated' && shelfType !== undefined && personUuid !== undefined,
    staleTime: 5 * 60_000,
    queryFn: () => loadSavedShelf(shelfType as string, personUuid as string),
  });

  // What to change is read when the person taps, before the change shows: by the time the
  // request runs, the shown state already has the book added or removed.
  const toggle = useMutation({
    mutationFn: async (change: {
      entry: SavedItem;
      save: boolean;
      lineages: readonly string[];
      shelves: SavedShelf['shelves'];
      edges: { shelfUuid: string; toUuid: string }[];
    }) => {
      if (change.save) {
        const scope = await writeScope();
        await saveToShelf(
          shelfType as string,
          shelfForScope(change.shelves, scope),
          change.entry.uuid ?? change.entry.lineageUuid,
          scope,
        );
      } else {
        await removeFromShelf(change.edges, change.shelves);
      }
    },
    onMutate: async ({ entry, save, lineages }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<SavedShelf>(key);
      const base = before ?? EMPTY_SHELF;
      const saved = new Map(base.saved);
      if (save) saved.set(entry.lineageUuid, saved.get(entry.lineageUuid) ?? []);
      else for (const lineage of lineages) saved.delete(lineage);
      queryClient.setQueryData<SavedShelf>(key, { ...base, saved });
      return { before };
    },
    onError: (_error, _input, context) => {
      queryClient.setQueryData(key, context?.before);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const saved = shelf.data?.saved ?? EMPTY_SHELF.saved;
  return {
    available: shelfType !== undefined,
    isSaved: (lineageUuid: string) => saved.has(lineageUuid),
    /** Saved lineages (books and Math rows), newest save last. */
    savedLineages: [...saved.keys()],
    /** Saves the item, or takes it off the shelf when it is already saved. */
    toggle: (entry: SavedItem) => {
      const lineages = entry.lineages ?? [entry.lineageUuid];
      toggle.mutate({
        entry,
        save: !lineages.some((lineage) => saved.has(lineage)),
        lineages,
        shelves: shelf.data?.shelves ?? EMPTY_SHELF.shelves,
        // Only edges Core returned: a save still being written has none to remove yet.
        edges: lineages
          .flatMap((lineage) => saved.get(lineage) ?? [])
          .filter((edge) => edge.toUuid),
      });
    },
    pending: toggle.isPending,
    failed: toggle.isError,
  };
}
